class TestimonialsSlider {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.track = wrapper.querySelector('[data-slider-track]');
    this.slides = Array.from(this.track.children);
    this.prevBtn = wrapper.querySelector('[data-slider-prev]');
    this.nextBtn = wrapper.querySelector('[data-slider-next]');
    this.dotsContainer = document.querySelector(`[data-section-id="${wrapper.closest('[data-section-id]').dataset.sectionId}"] [data-slider-dots]`);
    this.dots = this.dotsContainer ? Array.from(this.dotsContainer.children) : [];
    
    this.autoplay = wrapper.dataset.sliderAutoplay === 'true';
    this.speed = parseInt(wrapper.dataset.sliderSpeed) || 5000;
    this.loop = wrapper.dataset.sliderLoop === 'true';
    this.slidesDesktop = parseInt(wrapper.dataset.slidesDesktop) || 3;
    this.slidesMobile = parseInt(wrapper.dataset.slidesMobile) || 1;
    
    this.currentIndex = 0;
    this.slidesPerView = this.getSlidesPerView();
    this.maxIndex = Math.max(0, this.slides.length - this.slidesPerView);
    this.autoplayTimer = null;
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = 0;
    
    this.init();
  }
  
  init() {
    if (this.slides.length <= 1) return;
    
    this.updateSlidesPerView();
    this.addEventListeners();
    this.updateSlider();
    this.startAutoplay();
    this.observeResize();
  }
  
  getSlidesPerView() {
    return window.innerWidth >= 990 ? this.slidesDesktop : 
           window.innerWidth >= 750 ? Math.min(2, this.slidesDesktop) : 
           this.slidesMobile;
  }
  
  updateSlidesPerView() {
    this.slidesPerView = this.getSlidesPerView();
    this.maxIndex = Math.max(0, this.slides.length - this.slidesPerView);
    
    // Adjust current index if out of bounds
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.loop ? 0 : this.maxIndex;
    }
  }
  
  addEventListeners() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.prev();
        this.resetAutoplay();
      });
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.next();
        this.resetAutoplay();
      });
    }
    
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.resetAutoplay();
      });
    });
    
    // Touch events
    this.track.addEventListener('touchstart', this.touchStart.bind(this), { passive: true });
    this.track.addEventListener('touchend', this.touchEnd.bind(this));
    this.track.addEventListener('touchmove', this.touchMove.bind(this), { passive: true });
    
    // Mouse events for desktop drag
    this.track.addEventListener('mousedown', this.touchStart.bind(this));
    this.track.addEventListener('mouseup', this.touchEnd.bind(this));
    this.track.addEventListener('mouseleave', () => {
      if (this.isDragging) this.touchEnd();
    });
    this.track.addEventListener('mousemove', this.touchMove.bind(this));
    
    // Prevent context menu on long press
    this.track.addEventListener('contextmenu', (e) => {
      if (this.isDragging) e.preventDefault();
    });
    
    // Pause autoplay on hover
    this.wrapper.addEventListener('mouseenter', () => this.stopAutoplay());
    this.wrapper.addEventListener('mouseleave', () => this.startAutoplay());
    
    // Keyboard navigation
    this.wrapper.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
        this.resetAutoplay();
      } else if (e.key === 'ArrowRight') {
        this.next();
        this.resetAutoplay();
      }
    });
    
    this.wrapper.setAttribute('tabindex', '0');
  }
  
  touchStart(e) {
    this.isDragging = true;
    this.startPos = this.getPositionX(e);
    this.animationID = requestAnimationFrame(this.animation.bind(this));
    this.track.style.cursor = 'grabbing';
    this.stopAutoplay();
  }
  
  touchMove(e) {
    if (!this.isDragging) return;
    const currentPosition = this.getPositionX(e);
    const diff = currentPosition - this.startPos;
    this.currentTranslate = this.prevTranslate + diff;
  }
  
  touchEnd() {
    this.isDragging = false;
    cancelAnimationFrame(this.animationID);
    this.track.style.cursor = 'grab';
    
    const movedBy = this.currentTranslate - this.prevTranslate;
    const threshold = 50;
    
    if (movedBy < -threshold) {
      this.next();
    } else if (movedBy > threshold) {
      this.prev();
    } else {
      this.updateSlider();
    }
    
    this.startAutoplay();
  }
  
  getPositionX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
  }
  
  animation() {
    if (this.isDragging) {
      this.setSliderPosition(this.currentTranslate);
      requestAnimationFrame(this.animation.bind(this));
    }
  }
  
  setSliderPosition(position) {
    this.track.style.transform = `translateX(${position}px)`;
  }
  
  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else if (this.loop) {
      this.currentIndex = this.maxIndex;
    }
    this.updateSlider();
  }
  
  next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
    } else if (this.loop) {
      this.currentIndex = 0;
    }
    this.updateSlider();
  }
  
  goTo(index) {
    this.currentIndex = Math.min(index, this.maxIndex);
    this.updateSlider();
  }
  
  updateSlider() {
    const slideWidth = this.slides[0].getBoundingClientRect().width;
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    const translateX = -(this.currentIndex * (slideWidth + gap));
    
    this.track.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
    this.track.style.transform = `translateX(${translateX}px)`;
    this.prevTranslate = translateX;
    this.currentTranslate = translateX;
    
    this.updateArrows();
    this.updateDots();
  }
  
  updateArrows() {
    if (!this.loop) {
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
        this.prevBtn.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
        this.nextBtn.style.opacity = this.currentIndex >= this.maxIndex ? '0.3' : '1';
      }
    }
  }
  
  updateDots() {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('testimonials-dot--active', index === this.currentIndex);
    });
  }
  
  startAutoplay() {
    if (!this.autoplay || this.slides.length <= this.slidesPerView) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.next(), this.speed);
  }
  
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
  
  observeResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.updateSlidesPerView();
        this.updateSlider();
      }, 250);
    });
  }
}

// Initialize all sliders on the page
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('[data-slider]');
  sliders.forEach(slider => new TestimonialsSlider(slider));
});

// Re-initialize when Shopify section loads (customizer)
document.addEventListener('shopify:section:load', (e) => {
  const slider = e.target.querySelector('[data-slider]');
  if (slider) new TestimonialsSlider(slider);
});